import { MillerWeldMachine } from '@sb/integrations/implementations/WeldMachine/MillerWeldMachine/implementation/MillerWeldMachine';
import { WeldPositioner } from '@sb/integrations/implementations/WeldPositioner/implementation/WeldPositioner';
import type { RotationCompletionOutcome } from '@sb/integrations/implementations/WeldPositioner/implementation/WeldPositioner';
import type { WeldPositionerState } from '@sb/integrations/implementations/WeldPositioner/types';
import { makeNamespacedLog } from '@sb/log';
import type { ArmTarget, PoseArmTarget } from '@sb/motion-planning';
import type { EquipmentStateItem, StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import type {
  StepAction,
  StepPlayArguments,
} from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import type WaypointStep from '@sb/routine-runner/Step/Waypoint/Step';

import type { WeldMotionHandle } from '../weldArmTargets';
import {
  getWeldArmTargets,
  moveToWeldPosition,
  WeldTorchFramePin,
} from '../weldArmTargets';
import {
  getActiveWeldEquipment,
  getWeldTorchTCPOffsetOption,
  setWeldMachineParameters,
  startWeldMachine,
  stopWeldMachine,
} from '../weldMachineControl';

import {
  ROTARY_WELD_MAX_DURATION_SECONDS,
  RotaryWeldArguments,
} from './Arguments';
import { RunGate } from './RunGate';
import { RotaryWeldVariables } from './Variables';

const log = makeNamespacedLog('RotaryWeldStep');

/**
 * Ceiling on a weld that ends on the positioner's completion signal.
 *
 * `positionerSignal` carries no duration of its own, so without a cap a
 * positioner that never reports leaves the arc burning until someone notices.
 * Reuses the bound the timed variant already accepts rather than inventing a
 * second number for the operator to reason about.
 */
const COMPLETION_SIGNAL_TIMEOUT_MS = ROTARY_WELD_MAX_DURATION_SECONDS * 1000;

/**
 * The phases that have the arc live, so a resume out of one has to restrike it.
 *
 * `startArc` covers both the strike and the dwell that follows it: they run
 * under the same activity.
 */
const ARC_LIVE_ACTIVITIES = new Set<RotaryWeldVariables['currentActivity']>([
  'startArc',
  'startingPositioner',
  'rotating',
  'stoppingPositioner',
  'craterFilling',
]);

/**
 * The phases that have the part turning, so a resume out of one has to command
 * rotation again.
 *
 * `startingPositioner` belongs here as much as `rotating` does: a pause landing
 * while the rotate command is in flight has `_pause` stop the positioner, and
 * play then advances to `rotating` to wait on a completion edge that a
 * stationary part will never produce.
 */
const ROTATING_ACTIVITIES = new Set<RotaryWeldVariables['currentActivity']>([
  'startingPositioner',
  'rotating',
]);

export class RotaryWeldStep extends Step<
  RotaryWeldArguments,
  RotaryWeldVariables
> {
  public static areSubstepsRequired = true;

  public static Arguments = RotaryWeldArguments;

  public static Variables = RotaryWeldVariables;

  /**
   * Exactly one waypoint: the position the robot holds while the positioner
   * turns. `areSubstepsRequired` covers "must add one before running"; the
   * upper bound of one has no framework equivalent (there is no `maxSubsteps`)
   * and is enforced by `steps.RotaryWeld`.
   */
  public substeps: Array<WaypointStep> = [];

  /** A weld runs as long as the part takes; the self-timeout only logs. */
  public hasSelfTimeout(): boolean {
    return false;
  }

  private motionApproach?: WeldMotionHandle;

  private readonly torchFramePin = new WeldTorchFramePin();

  /**
   * Running, paused or stopped, and every way of waiting on it. One object so
   * that the states cannot disagree with each other and a transition is one
   * call rather than a flag write plus a set of waiters to wake by hand.
   */
  private readonly gate = new RunGate();

  /**
   * Serializes `_pause` against `_resume`. A resume that lands while pause is
   * still awaiting the arc stop would otherwise emit into the motion handle
   * first and leave `doMotion` stuck in its paused branch (the hazard
   * `WeldStep` documents as SUP-3787).
   */
  private pauseInFlight: Promise<void> = Promise.resolve();

  /** Serializes `shutDownWeld` against itself; see that method. */
  private shutdownInFlight: Promise<void> = Promise.resolve();

  /** Set once the arc strikes; see `failOnWelderError`. */
  private stopWatchingWelder?: () => void;

  protected initializeVariableState(): void {
    const { completedWeldCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedWeldCount,
      currentActivity: 'none',
      approachMovementActivity: 'none',
    };
  }

  protected async _play(playArgs: StepPlayArguments): Promise<void> {
    const { hasEncounteredStartStep } = playArgs;

    if (!hasEncounteredStartStep()) {
      return;
    }

    // Cleared per play: a loop iteration replays this instance, and the
    // restore below must key on what THIS play pinned.
    this.torchFramePin.reset();
    this.gate.reset();

    const preStepTCPOption =
      this.routineContext.getRoutineRunnerState().kinematicState
        .tcpOffsetOption;

    try {
      await this.playRotaryWeld(playArgs);
    } finally {
      this.torchFramePin.restore(this.routineContext, preStepTCPOption);
    }
  }

  private async playRotaryWeld(playArgs: StepPlayArguments): Promise<void> {
    const { fail } = playArgs;

    // A weld that cannot stop its own positioner must not start one.
    const positioner = this.getPositioner();

    if (positioner == null) {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason:
          'Routine stopped: this rotary weld has no weld positioner selected.',
      });

      return;
    }

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason:
          'Routine stopped: this rotary weld has no weld machine selected.',
      });

      return;
    }

    try {
      await this.runWeld(playArgs, { positioner, weldMachine });
    } finally {
      this.stopWatchingWelder?.();
      delete this.stopWatchingWelder;

      // Whatever happened above — a failure, a stop, a throw out of the
      // equipment layer — the cell must not be left with a live arc or a
      // turning part. Both commands tolerate having already been sent, so the
      // paths that shut down in order on their way out stay as they are and
      // this only has to catch what they miss.
      await this.shutDownWeld({ weldMachine, fail });
    }
  }

  /**
   * The weld proper, from the approach through crater fill.
   *
   * Split from `playRotaryWeld` so that everything past the equipment guards
   * runs inside that method's shutdown `finally`.
   */
  private async runWeld(
    playArgs: StepPlayArguments,
    {
      positioner,
      weldMachine,
    }: { positioner: WeldPositioner; weldMachine: EquipmentStateItem },
  ): Promise<void> {
    const { fail } = playArgs;

    // 1. Move to the single waypoint and hold.
    if (!(await this.approachWeldPosition(playArgs))) {
      return;
    }

    // A pause that lands after the approach resolves but before the arc
    // strikes would otherwise run straight through alignment, parameters and
    // the strike while the routine is paused.
    if ((await this.gate.whenRunning()) !== 'running') {
      return;
    }

    // 2. Alignment gate, before anything is energized.
    if (!(await this.checkAlignment({ fail }))) {
      return;
    }

    // 3. Weld parameters, then the arc.
    this.setVariable('currentActivity', 'settingWeldParameters');

    if (!(await this.sendWeldParameters({ weldMachine, fail }))) {
      return;
    }

    // Sending parameters is an equipment round-trip, and the next line strikes
    // an arc.
    if ((await this.gate.whenRunning()) !== 'running') {
      return;
    }

    this.setVariable('currentActivity', 'startArc');

    // A refused strike must end the weld here. Falling through would turn the
    // part under a dead torch, and whether the routine's own stop got here
    // first would come down to how long the arc-start dwell is.
    if (!(await this.startArc({ weldMachine, fail }))) {
      return;
    }

    this.stopWatchingWelder = this.failOnWelderError({ fail });

    // 4. Hold the arc before rotation begins. PRD: "The arc must start before
    //    the positioner begins rotating."
    if (!(await this.gate.wait(this.args.arcStartTime))) {
      return;
    }

    // 5. Arm the completion detector BEFORE commanding rotation: that order
    //    cannot miss the edge, and it reads its baseline while the part is
    //    still at rest, which is what tells a home-index sensor apart from one
    //    that has just come round.
    const rotation =
      this.args.completion.kind === 'positionerSignal'
        ? positioner.awaitRotationComplete()
        : undefined;

    this.setVariable('currentActivity', 'startingPositioner');

    if (!(await this.commandPositioner('rotate', { fail }))) {
      rotation?.cancel();

      return;
    }

    // 6. Wait for the configured completion condition.
    this.setVariable('currentActivity', 'rotating');

    let didComplete = false;

    try {
      didComplete = await this.awaitCompletion({ rotation, fail });
    } finally {
      // The positioner stops on every exit, including a throw — leaving it
      // turning with the arc off would keep dragging the part past the weld.
      this.setVariable('currentActivity', 'stoppingPositioner');
      await this.commandPositioner('stop', { fail });
    }

    if (!didComplete) {
      await this.stopArc({ weldMachine, fail });

      return;
    }

    // 7. Crater fill with the part stopped, then arc off.
    //
    //    Gated like every other dwell in the step, so the taper is charged
    //    only running time and gets its whole configured duration of arc
    //    rather than however much of it a pause happened not to eat. A pause
    //    here parks at `craterFilling`, which is arc-live, so the resume
    //    restrikes and the remaining taper runs; that restrike is the cost of
    //    finishing the taper at all, and it is the same one the operator gets
    //    resuming out of `rotating`.
    this.setVariable('currentActivity', 'craterFilling');

    if (!(await this.gate.wait(this.args.craterFillTime))) {
      // Stopped mid-taper. `_stop` has already shut the weld down; this only
      // gets out of the way without writing over the activity it left.
      return;
    }

    this.setVariable('currentActivity', 'stopArc');
    await this.stopArc({ weldMachine, fail });

    // A pause or stop landing during the arc-off round-trip: an interrupted
    // weld is not a completed one.
    if ((await this.gate.whenRunning()) !== 'running') {
      return;
    }

    this.setVariable('currentActivity', 'none');

    this.setVariable(
      'completedWeldCount',
      this.variables.completedWeldCount + 1,
    );
  }

  /**
   * Move to the waypoint that defines the weld position and stay there.
   *
   * The step pins the global motion frame to the torch for its target math,
   * exactly as `WeldStep` does, so a relative-to-current waypoint resolves
   * against the torch rather than whatever frame the operator last chose.
   */
  private async approachWeldPosition({
    fail,
  }: StepPlayArguments): Promise<boolean> {
    this.setVariable('currentActivity', 'approaching');

    this.torchFramePin.pin(this.routineContext, this.getTCPOffsetOption());

    const substeps = this.substeps.filter((s) => !s.isEffectivelySuppressed());

    // `RotaryWeldRule.checkWaypointCount` counts children without regard to
    // suppression, so the sole waypoint being suppressed arrives here as an
    // empty list — and `getWeldArmTargets` reports no failure of its own for
    // it, which would turn the whole step into a silent no-op.
    if (substeps.length === 0) {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason:
          'Routine stopped: this rotary weld has no waypoint to weld at.',
      });

      return false;
    }

    const targets = await getWeldArmTargets({
      routineContext: this.routineContext,
      getTCPOffsetOption: () => this.getTCPOffsetOption(),
      completedWeldCount: this.variables.completedWeldCount,
      substeps,
      fail,
      convertToPose: true,
    });

    const armTarget: (ArmTarget & PoseArmTarget) | undefined = targets?.[0];

    if (armTarget == null) {
      // `getWeldArmTargets` reports its own failures; the no-waypoint case is
      // already answered above.
      return false;
    }

    return moveToWeldPosition({
      routineContext: this.routineContext,
      armTarget,
      speedProfile: this.args.approachSpeedProfile,
      stepID: this.id,
      fail,
      getApproachActivity: () => this.variables.approachMovementActivity,
      setApproachActivity: (activity) =>
        this.setVariable('approachMovementActivity', activity),
      setMotionHandle: (motion) => {
        if (motion == null) {
          delete this.motionApproach;
        } else {
          this.motionApproach = motion;
        }
      },
    });
  }

  /**
   * @returns whether the arc may be struck.
   */
  private async checkAlignment({
    fail,
  }: {
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<boolean> {
    if (!this.args.alignmentRequired) {
      return true;
    }

    this.setVariable('currentActivity', 'checkingAlignment');

    const isAligned = this.readPositionerState()?.isAligned ?? null;

    // Treating an unanswerable gate as "aligned" would strike the arc on the
    // operator's behalf, which is what the gate exists to prevent.
    if (isAligned == null) {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason:
          'Routine stopped: alignment is required but the weld positioner has no alignment sensor configured.',
      });

      return false;
    }

    if (!isAligned) {
      // PRD: the arc must not start and the routine stops, with a toast and
      // an Alerts entry the operator can open for the reason. The operator
      // rotates the part by hand and restarts, so this is a failure rather
      // than a pause.
      await fail({
        failure: { kind: FailureKind.WeldPositionerNotAligned },
        failureReason: 'Routine stopped: the weld positioner is not aligned.',
      });

      return false;
    }

    return true;
  }

  /**
   * Resolve when the weld's completion condition is met.
   *
   * @returns whether the weld ran to completion, as opposed to being paused
   * into a stop, timing out, or losing its positioner.
   */
  private async awaitCompletion({
    rotation,
    fail,
  }: {
    rotation?: {
      completed: Promise<RotationCompletionOutcome>;
      cancel: () => void;
    };
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<boolean> {
    const { completion } = this.args;

    if (completion.kind === 'time') {
      return this.gate.wait(completion.durationSeconds * 1000);
    }

    if (rotation == null) {
      return false;
    }

    // The cap is the only thing between a positioner that never reports and
    // an arc that burns until an operator intervenes. The gate counts it in
    // welding time and drops it when this race settles.
    let outcome: RotationCompletionOutcome | 'stopped' | 'timedOut';

    try {
      outcome = await this.gate.race(rotation.completed, {
        timeoutMs: COMPLETION_SIGNAL_TIMEOUT_MS,
      });
    } finally {
      rotation.cancel();
    }

    if (outcome === 'completed') {
      return true;
    }

    // Stop is not a failure and reports nothing: `_stop` has already put the
    // cell back to rest, and this only has to stop waiting on an edge that a
    // stationary part will never produce.
    if (outcome === 'stopped') {
      return false;
    }

    if (outcome === 'timedOut') {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Routine stopped: the weld positioner did not report the rotation finishing within ${ROTARY_WELD_MAX_DURATION_SECONDS} seconds of welding.`,
      });

      return false;
    }

    if (outcome === 'destroyed') {
      await fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason:
          'Routine stopped: the weld positioner was removed while the weld was running.',
      });
    }

    return false;
  }

  /**
   * Fail the routine if the selected welder reports an error. The machine
   * reports one hit mid-weld only through its status, never as a command
   * error, so without this the weld carries on and counts the part with no
   * arc. The failure stops this step, and `_stop` puts the cell back to rest.
   *
   * @returns the unsubscribe. A device that is not a `MillerWeldMachine`
   * (the simulator, say) is not watched.
   */
  private failOnWelderError({
    fail,
  }: {
    fail: (failure: StepFailure) => Promise<void>;
  }): () => void {
    const { selectedMachineID } = this.args;

    const device =
      selectedMachineID == null
        ? undefined
        : this.routineContext.equipment.getDeviceById(selectedMachineID);

    if (!(device instanceof MillerWeldMachine)) {
      return () => {};
    }

    return device.on('stateChange', () => {
      const { discreteInputs, inputRegisters } = device.update();

      if (discreteInputs.errorActive) {
        fail({
          failure: { kind: FailureKind.WeldFailure },
          failureReason: `Routine stopped: the weld machine reported an error (code ${inputRegisters.errorCode}).`,
        });
      }
    });
  }

  private get activeEquipment(): {
    weldMachine: EquipmentStateItem | undefined;
    torch: EquipmentStateItem | undefined;
  } {
    return getActiveWeldEquipment({
      routineContext: this.routineContext,
      selectedMachineID: this.args.selectedMachineID,
      selectedTorchID: this.args.selectedTorchID,
    });
  }

  private getTCPOffsetOption() {
    return getWeldTorchTCPOffsetOption({
      torch: this.activeEquipment.torch,
      selectedTorchID: this.args.selectedTorchID,
    });
  }

  /** @returns whether the machine accepted them; see `startArc`. */
  private async sendWeldParameters({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<boolean> {
    return setWeldMachineParameters({
      routineContext: this.routineContext,
      selectedMachineID: this.args.selectedMachineID,
      weldMachine,
      millerWeldParameters: this.args.millerWeldParameters,
      fail,
    });
  }

  /**
   * @returns whether the arc struck. On `false` the failure is already
   * reported, and since `fail` does not throw back, checking this is the only
   * thing that keeps the caller from going on to turn the part.
   */
  private async startArc({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<boolean> {
    return startWeldMachine({
      routineContext: this.routineContext,
      selectedMachineID: this.args.selectedMachineID,
      weldMachine,
      fail,
    });
  }

  /**
   * Tolerates an arc that never struck: the stop command is safe to send
   * either way, which is what lets `_stop` call it without tracking whether
   * the weld got that far.
   */
  private async stopArc({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<void> {
    await stopWeldMachine({
      routineContext: this.routineContext,
      selectedMachineID: this.args.selectedMachineID,
      weldMachine,
      fail,
    });
  }

  /**
   * Put the cell back to rest: arc off, then the positioner's trigger
   * released. That order matters — a part held still under a live arc burns
   * through.
   *
   * Serialized against itself because the play loop's `finally` and a
   * concurrent `_stop` can both reach it, and two of these in flight at the
   * equipment layer is not something either caller can reason about.
   */
  private async shutDownWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem | undefined;
    fail: (failure: StepFailure) => Promise<void>;
  }): Promise<void> {
    const previous = this.shutdownInFlight;

    // Reported without waiting: the failure stops the routine, and that stop
    // comes back into this method and queues behind the shutdown that is
    // waiting on it. The runner contains its own rejections.
    const reportFailure = (failure: StepFailure): Promise<void> => {
      fail(failure);

      return Promise.resolve();
    };

    this.shutdownInFlight = (async () => {
      await previous.catch(() => {});

      if (weldMachine != null) {
        await this.stopArc({ weldMachine, fail: reportFailure });
      }

      await this.commandPositioner('stop', { fail: reportFailure });
    })();

    await this.shutdownInFlight;
  }

  /**
   * The selected positioner device, or null when no `WeldPositioner` with that
   * id is loaded.
   *
   * The device rather than its state, because a weld ending on the completion
   * signal waits on `awaitRotationComplete`, which state cannot express.
   */
  private getPositioner(): WeldPositioner | null {
    const { selectedPositionerID } = this.args;

    if (selectedPositionerID == null) {
      return null;
    }

    const device =
      this.routineContext.equipment.getDeviceById(selectedPositionerID);

    return device instanceof WeldPositioner ? device : null;
  }

  private readPositionerState(): WeldPositionerState | null {
    const positioner = this.getPositioner();

    return positioner == null
      ? null
      : (positioner.update() as WeldPositionerState);
  }

  /**
   * Drive the positioner through the device, which owns its ports.
   *
   * @returns whether the command was accepted. A failed rotate that reported
   * only through `fail` would fall through into the completion wait and book a
   * finished weld on a part that never turned — `fail` does not throw back,
   * because `RoutineRunner.pauseOrFail` swallows it.
   */
  private async commandPositioner(
    action: 'rotate' | 'stop',
    { fail }: { fail: (failure: StepFailure) => Promise<void> },
  ): Promise<boolean> {
    const { selectedPositionerID } = this.args;

    if (selectedPositionerID == null) {
      return false;
    }

    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'WeldPositionerCommand',
        deviceID: selectedPositionerID,
        subCommand: { kind: action },
      });

      return true;
    } catch (error) {
      await fail({
        failure: { kind: FailureKind.IOFailure },
        failureReason: `Failed to ${action} the weld positioner`,
        error,
      });

      return false;
    }
  }

  /**
   * Pause stops the weld without ending it: arc off, then positioner stopped.
   * That order matters — a part held still under a live arc burns through.
   *
   * The countdown stops with it, so a timed weld resumes with the time it had
   * left rather than having bled it away while nothing was turning.
   */
  protected async _pause({ fail }: StepAction): Promise<void> {
    log.info('_pause', 'Pausing rotary weld');

    // Asked for up front, matching `_stop`: a dwell in flight stops counting
    // at the moment the pause was requested rather than one or two equipment
    // round-trips later.
    this.gate.pause();

    this.pauseInFlight = (async () => {
      const { weldMachine } = this.activeEquipment;

      if (weldMachine != null) {
        await this.stopArc({ weldMachine, fail });
      }

      await this.commandPositioner('stop', { fail });

      this.motionApproach?.emit('pause');
    })();

    await this.pauseInFlight;
  }

  /**
   * Resume puts back what pause took away, in the order the weld started in:
   * parameters, arc, rotation.
   *
   * What to put back is read off `currentActivity`, which says where play is
   * parked. `_pause` deliberately leaves it alone for that reason — the UI
   * learns the step is paused from the routine's own state, and from
   * `approachMovementActivity` during the approach.
   *
   * No arc-start dwell on the way back — the base metal is still hot, and the
   * dwell exists to establish a puddle from cold.
   */
  protected async _resume({ fail }: StepAction): Promise<void> {
    log.info('_resume', 'Resuming rotary weld');

    // See `pauseInFlight`: emitting 'resume' into the motion handle before
    // pause has emitted 'pause' leaves the motion stuck paused.
    await this.pauseInFlight;

    this.motionApproach?.emit('resume');

    // Where play is parked, which is what says what has to come back. The
    // restore below sets activities of its own, so this is put back before the
    // gate is released and play carries on from it.
    const parkedAt = this.variables.currentActivity;

    if (ARC_LIVE_ACTIVITIES.has(parkedAt)) {
      const { weldMachine } = this.activeEquipment;

      if (weldMachine != null) {
        this.setVariable('currentActivity', 'settingWeldParameters');

        // A refused restrike leaves the gate paused and the part still. The
        // failure it reported stops the routine, and `_stop` lets play go.
        if (!(await this.sendWeldParameters({ weldMachine, fail }))) {
          return;
        }

        this.setVariable('currentActivity', 'startArc');

        if (!(await this.startArc({ weldMachine, fail }))) {
          return;
        }
      }
    }

    if (ROTATING_ACTIVITIES.has(parkedAt)) {
      this.setVariable('currentActivity', 'startingPositioner');
      await this.commandPositioner('rotate', { fail });
    }

    this.setVariable('currentActivity', parkedAt);

    // Released last, so no time is charged against a weld whose arc and
    // rotation are not back yet.
    this.gate.resume();
  }

  /**
   * Stop must leave the cell safe: arc off, then the positioner's trigger
   * released, then the approach motion cancelled. Tolerates being called
   * before the arc ever struck.
   */
  protected async _stop({ fail }: StepAction): Promise<void> {
    log.info('_stop', 'Stopping rotary weld');

    // Stopped before the equipment commands rather than after them. Those are
    // two round-trips, and until the waiters are let go `_play` is parked on a
    // dwell with the stop already decided — time in which it could otherwise
    // let that dwell expire and energize the next phase.
    this.gate.stop();

    this.setVariable('currentActivity', 'stopArc');

    await this.shutDownWeld({
      weldMachine: this.activeEquipment.weldMachine,
      fail,
    });

    this.motionApproach?.emit('cancel');

    this.setVariable('currentActivity', 'none');
  }
}
